import { useState } from 'react';
import { LineChart, Sparkles, Info, CalendarClock, HelpCircle, Trophy } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Table, type Column } from '@/shared/components/ui/Table';
import { useForecast } from '../hooks/useAnalytics';
import { PanelState } from './PanelState';
import { formatCurrency } from '../constants';
import type { ForecastPeriod, CurrencyTotal } from '../types';

/**
 * The factors that feed the optional AI probability overlay. When the forecast
 * is AI-weighted, its per-deal factor comes from `ai.js` `dealWinProbability`,
 * which is computed from these inputs. Listing them keeps the AI_Suggestion
 * traceable to real data rather than a black-box number (Req 14.5).
 */
const AI_CONTRIBUTING_FACTORS = [
  'Deal stage',
  'Deal value',
  'Deal age',
  'Linked-lead call activity',
];

/** Render each currency total on its own line — currencies are never summed (Req 6.6). */
function CurrencyTotals({ totals }: { totals: CurrencyTotal[] }) {
  if (!totals || totals.length === 0) {
    return <span className="text-on-surface-variant">—</span>;
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      {totals.map((t) => (
        <span key={t.currency} className="tabular-nums">
          {formatCurrency(t.total, t.currency)}
        </span>
      ))}
    </div>
  );
}

/** A labeled block of per-currency totals (used for unscheduled + realized-won). */
function CurrencyBlock({ totals }: { totals: CurrencyTotal[] }) {
  if (!totals || totals.length === 0) {
    return <p className="text-body-sm text-on-surface-variant">Nothing to report.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {totals.map((t) => (
        <span
          key={t.currency}
          className="rounded border border-outline px-3 py-1 text-data-md tabular-nums text-on-surface"
        >
          {formatCurrency(t.total, t.currency)}
        </span>
      ))}
    </div>
  );
}

export function ForecastPanel({ enabled }: { enabled: boolean }) {
  // Toggle between the default rep-entered probability forecast (Req 6.3) and
  // the AI-weighted overlay (`?factor=ai`, Req 6.8).
  const [aiWeighted, setAiWeighted] = useState(false);

  const { data, isLoading, isError } = useForecast({ aiWeighted, enabled });

  // Trust the server's echoed flag rather than the local toggle so the label
  // always reflects what actually produced these numbers (Req 6.8, 14.3).
  const isAiSuggestion = !!data?.aiWeighted;

  const periods = data?.periods ?? [];

  const periodColumns: Column<ForecastPeriod>[] = [
    {
      key: 'period',
      header: 'Period',
      render: (r) => (
        <span className="flex items-center gap-2 font-medium text-on-surface">
          <CalendarClock className="h-4 w-4 text-on-surface-variant" />
          {r.period}
        </span>
      ),
    },
    {
      key: 'totals',
      header: 'Weighted forecast by currency',
      align: 'right',
      render: (r) => <CurrencyTotals totals={r.totalsByCurrency} />,
    },
  ];

  return (
    <Card
      // Distinguish the whole surface as an AI_Suggestion when AI-weighted
      // (Req 6.8, 14.3) — a colored ring separates it from persisted data.
      className={isAiSuggestion ? 'ring-2 ring-info/50' : undefined}
    >
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <LineChart className="h-4 w-4" /> Revenue Forecast
            {isAiSuggestion && (
              <Badge tone="info" className="gap-1">
                <Sparkles className="h-3 w-3" /> AI Suggestion
              </Badge>
            )}
          </span>
        }
        subtitle={
          isAiSuggestion
            ? 'AI-weighted forecast. Probability factors are AI-computed, not rep-entered.'
            : 'Weighted expected revenue for open deals, using the rep-entered win probability.'
        }
        action={
          <Button
            variant={aiWeighted ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setAiWeighted((v) => !v)}
            aria-pressed={aiWeighted}
          >
            <Sparkles className="h-4 w-4" />
            {aiWeighted ? 'AI weighting on' : 'AI weighting'}
          </Button>
        }
      />
      <CardBody className="space-y-6">
        {/* When AI-weighted, expose the contributing factors so the suggestion
            is traceable to real deal data (Req 14.5). */}
        {isAiSuggestion && (
          <div className="flex items-start gap-2 rounded-card border border-info/40 bg-info/5 p-3 text-body-sm text-on-surface-variant">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-info-text" />
            <div className="space-y-1">
              <p className="font-medium text-on-surface">
                This entire forecast is an AI Suggestion.
              </p>
              <p>
                Each open deal&apos;s probability factor is computed by the win-probability model
                from these contributing factors:
              </p>
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {AI_CONTRIBUTING_FACTORS.map((f) => (
                  <Badge key={f} tone="info">
                    {f}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        <PanelState
          isLoading={isLoading}
          isError={isError}
          isEmpty={!!data?.empty}
          emptyTitle="No forecast data"
          emptyDescription="No permitted deals exist to forecast yet, so there is nothing to report."
          emptyIcon={<LineChart className="h-6 w-6" />}
        >
          {/* Dated forecast periods (Req 6.4). */}
          <section className="space-y-2">
            <h4 className="text-data-md font-semibold text-on-surface">Forecast by period</h4>
            <Table
              columns={periodColumns}
              data={periods}
              rowKey={(r) => r.period}
              empty="No dated forecast periods."
            />
          </section>

          {/* Unscheduled bucket — open deals with no valid close date (Req 6.5). */}
          <section className="space-y-2">
            <h4 className="flex items-center gap-2 text-data-md font-semibold text-on-surface">
              <HelpCircle className="h-4 w-4 text-on-surface-variant" /> Unscheduled
            </h4>
            <p className="text-body-sm text-on-surface-variant">
              Open deals with no valid expected close date. Not assigned to any period.
            </p>
            <CurrencyBlock totals={data?.unscheduled ?? []} />
          </section>

          {/* Realized (closed_won) revenue — separate from forecast (Req 6.2). */}
          <section className="space-y-2">
            <h4 className="flex items-center gap-2 text-data-md font-semibold text-on-surface">
              <Trophy className="h-4 w-4 text-success-text" /> Realized (won) revenue
            </h4>
            <CurrencyBlock totals={data?.realizedWon ?? []} />
            {data?.notes?.realizedWon && (
              <p className="flex items-start gap-2 text-body-sm text-on-surface-variant">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                {data.notes.realizedWon}
              </p>
            )}
          </section>
        </PanelState>
      </CardBody>
    </Card>
  );
}
